import type IntegrationProvider from "../models/integrationProvider";
import type IntegrationSetupDto from "../../integration_setup/models/IntegrationSetup";
import GlassContainer from "@/shared/components/glasscontainer";
import { useIconAccent } from "@/shared/lib/iconAccent";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2, Power, Settings, Play, Globe } from "lucide-react";
import NoIcon from "@/assets/icons/no_icon.svg";

const SETUP_STATUS: Record<number, { label: string; badge: string; dot: string }> = {
  0: { label: "Configurado", badge: "bg-emerald-500/10 text-emerald-600 border-emerald-500/15", dot: "bg-emerald-500" },
  1: { label: "Inactivo", badge: "bg-gray-500/10 text-gray-600 border-gray-500/15", dot: "bg-gray-400" },
  2: { label: "Error", badge: "bg-red-500/10 text-red-600 border-red-500/15", dot: "bg-red-500 animate-pulse" },
};

const NO_SETUP_STATUS = {
  label: "Sin configurar",
  badge: "bg-amber-500/10 text-amber-600 border-amber-500/15",
  dot: "bg-amber-500",
};

export interface IntegrationCardProps {
  integration: IntegrationProvider;
  setup?: IntegrationSetupDto;
  onDeleteProvider: (id: string) => void;
  onToggleGlobal?: (id: string) => void;
  onTestSetup: (providerId: string) => void;
}

const ACTION_BUTTON =
  "size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:shadow-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

export function IntegrationCard({
  integration,
  setup,
  onDeleteProvider,
  onToggleGlobal,
  onTestSetup,
}: IntegrationCardProps) {
  const navigate = useNavigate();
  const hasSetup = !!setup;
  const setupStatus = setup ? (SETUP_STATUS[setup.status as number] ?? SETUP_STATUS[1]) : NO_SETUP_STATUS;
  const accent = useIconAccent(integration.icon || null, integration.id);

  const handleDelete = () => {
    const confirmed = confirm(
      `¿Eliminar "${integration.name}" y toda su configuración?`,
    );
    if (confirmed) onDeleteProvider(integration.id);
  };

  return (
    <GlassContainer className="group relative rounded-3xl transition-all duration-500 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] overflow-hidden hover:border-white hover:bg-glass-shine hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)]">
      <div className="p-5 relative flex flex-col min-w-0">
        <div className="flex items-start gap-3.5 min-w-0">
          <div
            className={`flex size-12 shrink-0 items-center justify-center rounded-2xl shadow-lg transition-transform group-hover:scale-110 group-hover:rotate-3 duration-500 ${
              accent ? "" : "bg-[#0071e3] shadow-[#0071e3]/30"
            }`}
            style={
              accent
                ? { backgroundColor: `rgb(${accent})`, boxShadow: `0 8px 20px -4px rgba(${accent}, 0.45)` }
                : undefined
            }
          >
            <span className="flex size-9 items-center justify-center rounded-xl bg-white shadow-sm overflow-hidden">
              <img
                src={integration.icon || NoIcon}
                alt={integration.name}
                className="size-7 object-contain"
              />
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-bold text-foreground/90 tracking-tight group-hover:text-accent transition-colors">
              {integration.name}
            </h3>
            <p className="mt-0.5 text-[11px] text-foreground/40 font-medium truncate">
              {integration.category} · {integration.authType}
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {hasSetup && (
              <button
                onClick={() => onTestSetup(integration.id)}
                className={`${ACTION_BUTTON} hover:bg-emerald-500 hover:border-emerald-500 hover:text-white`}
                title="Probar"
              >
                <Play className="size-3.5" />
              </button>
            )}
            <button
              onClick={() => navigate(`/admin/integrations/setups/${integration.id}`)}
              className={`${ACTION_BUTTON} hover:bg-white hover:text-accent`}
              title={hasSetup ? "Credenciales" : "Configurar"}
            >
              <Settings className="size-3.5" />
            </button>
            <button
              onClick={() => navigate(`/admin/integrations/${integration.id}`)}
              className={`${ACTION_BUTTON} hover:bg-white hover:text-accent`}
              title="Editar"
            >
              <Edit2 className="size-3.5" />
            </button>
            {onToggleGlobal && (
              <button
                onClick={() => onToggleGlobal(integration.id)}
                className={`${ACTION_BUTTON} hover:bg-white hover:text-accent`}
                title={integration.IsGlobal ? "Hacer local" : "Hacer global"}
              >
                <Power className="size-3.5" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className={`${ACTION_BUTTON} hover:bg-red-500 hover:border-red-500 hover:text-white`}
              title="Eliminar"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        </div>

        {integration.description && (
          <p className="mt-3 text-[12px] text-foreground/50 leading-relaxed line-clamp-2">
            {integration.description}
          </p>
        )}

        <div className="mt-4 flex items-center gap-1.5 flex-wrap min-w-0">
          <span
            className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 ${setupStatus.badge}`}
          >
            <span className={`size-1.5 rounded-full ${setupStatus.dot}`} />
            {setupStatus.label.toUpperCase()}
          </span>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 ${
              integration.IsGlobal
                ? "bg-accent/10 text-accent border-accent/15"
                : "bg-gray/10 text-gray border-black/5"
            }`}
          >
            {integration.IsGlobal && <Globe className="size-3" />}
            {integration.IsGlobal ? "GLOBAL" : "LOCAL"}
          </span>
        </div>
      </div>
    </GlassContainer>
  );
}
